import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, MapPin, ArrowRight, ArrowLeft, Navigation, Upload, X, Image as ImageIcon, Plus, Trash2, ChevronDown, ChevronUp, Briefcase, Calendar, Clock, Sparkles, Camera, CheckCircle2, Info, Shield, FileText, RotateCcw } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import { usePhotoLightbox } from '../components/PhotoLightbox';
import axios from 'axios';
import { PICKUP_TIME_SLOTS, isPickupSlotDisabled } from '../lib/pickupSlots';
import { openRazorpayCheckout } from '../lib/razorpay';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { EditPriceConfirmModal } from '../components/EditPriceConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYMENT_PROVIDER = (process.env.REACT_APP_PAYMENT_PROVIDER || 'stripe').toLowerCase();

const BAG_TYPES = [
  { value: 'bag', label: 'BAG' },
  { value: 'trolley', label: 'TROLLEY' },
  { value: 'suitcase', label: 'SUITCASE' },
  { value: 'backpack', label: 'BACKPACK' },
  { value: 'duffle', label: 'DUFFLE BAG' },
  { value: 'box', label: 'BOX / CARTON' },
  { value: 'other', label: 'OTHER' }
];

const MATERIALS = [
  { value: 'soft_fabric', label: 'SOFT FABRIC' },
  { value: 'hard_shell', label: 'HARD SHELL' },
  { value: 'leather', label: 'LEATHER' },
  { value: 'plastic', label: 'PLASTIC' }
];

const HANDLING_TYPES = [
  { value: 'small',     label: 'SMALL (< 10 KG)' },
  { value: 'medium',    label: 'MEDIUM (10 – 15 KG)' },
  { value: 'heavy',     label: 'HEAVY (15 – 20 KG)' },
  { value: 'oversized', label: 'OVERSIZED (> 20 KG)' }
];

const deriveHandlingFromWeight = (w) => {
  const kg = parseFloat(w);
  if (!kg || kg < 10) return 'small';
  if (kg < 15) return 'medium';
  if (kg < 20) return 'heavy';
  return 'oversized';
};

const LOCK_TYPES = [
  { value: 'tsa', label: 'TSA LOCK' },
  { value: 'combination', label: 'COMBINATION LOCK' },
  { value: 'key', label: 'KEY LOCK' },
  { value: 'none', label: 'NO LOCK' }
];

// Standard bag colour palette. Hex value is used only for the swatch preview;
// the field value stored in the booking is the human-readable label so the
// agent/partner can read it on the printed baggage tag without colour rendering.
const BAG_COLORS = [
  { label: 'Black',       hex: '#111111' },
  { label: 'White',       hex: '#FFFFFF' },
  { label: 'Grey',        hex: '#808080' },
  { label: 'Silver',      hex: '#C0C0C0' },
  { label: 'Navy Blue',   hex: '#0A1F44' },
  { label: 'Royal Blue',  hex: '#1E40AF' },
  { label: 'Sky Blue',    hex: '#7DD3FC' },
  { label: 'Teal',        hex: '#0D9488' },
  { label: 'Green',       hex: '#15803D' },
  { label: 'Olive',       hex: '#65A30D' },
  { label: 'Yellow',      hex: '#FACC15' },
  { label: 'Orange',      hex: '#F97316' },
  { label: 'Red',         hex: '#DC2626' },
  { label: 'Maroon',      hex: '#7F1D1D' },
  { label: 'Pink',        hex: '#EC4899' },
  { label: 'Purple',      hex: '#7C3AED' },
  { label: 'Brown',       hex: '#78350F' },
  { label: 'Tan',         hex: '#D4A373' },
  { label: 'Beige',       hex: '#F5E6CA' },
  { label: 'Multi-colour', hex: 'linear-gradient(135deg,#DC2626 0%,#FACC15 33%,#15803D 66%,#1E40AF 100%)' }
];

const createNewBag = () => ({
  id: Date.now() + Math.random(),
  bag_type: 'suitcase',
  brand: '',
  color: '',
  material: 'soft_fabric',
  weight: '',
  dimensions: '',
  handling_type: 'small',
  lock_type: 'tsa',
  distinguishing_marks: '',
  declared_value: '',
  insurance_opted: false,
  images: []
});

// Click-to-open colour picker. The text input is the trigger — focusing or
// clicking it pops up the swatch palette underneath. Selecting a swatch writes
// the colour NAME into the field and closes the palette. The text input
// remains editable so customers can still type a custom shade.
const BagColorPicker = ({ idx, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const pick = (label) => {
    onChange(label);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={`bag-color-wrapper-${idx}`}>
      <div className="relative">
        <input
          data-testid={`bag-color-${idx}`}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          placeholder="Tap to pick from palette · or type a custom shade"
          className="w-full pr-10"
          autoComplete="off"
        />
        {(() => {
          const matched = BAG_COLORS.find(
            c => c.label.toLowerCase() === (value || '').trim().toLowerCase()
          );
          if (!matched) return null;
          return (
            <span
              data-testid={`bag-color-preview-${idx}`}
              aria-hidden="true"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 border-2 border-black pointer-events-none"
              style={matched.hex.startsWith('linear-gradient') ? { background: matched.hex } : { backgroundColor: matched.hex }}
            />
          );
        })()}
      </div>
      {open && (
        <div
          data-testid={`bag-color-palette-${idx}`}
          className="absolute z-30 left-0 right-0 mt-1 p-2 border-2 border-black bg-white shadow-[6px_6px_0_0_rgba(0,0,0,1)] max-h-[260px] overflow-y-auto"
        >
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5">
            {BAG_COLORS.map((c) => {
              const selected = (value || '').trim().toLowerCase() === c.label.toLowerCase();
              return (
                <button
                  key={c.label}
                  type="button"
                  data-testid={`bag-color-swatch-${idx}-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => pick(c.label)}
                  title={c.label}
                  aria-label={c.label}
                  aria-pressed={selected}
                  className={`relative w-full aspect-square border-2 transition-transform hover:scale-110 ${selected ? 'border-[#FF4500] ring-2 ring-[#FF4500]' : 'border-black'}`}
                  style={c.hex.startsWith('linear-gradient') ? { background: c.hex } : { backgroundColor: c.hex }}
                >
                  {selected && (
                    <span
                      className="absolute inset-0 flex items-center justify-center text-base font-black"
                      style={{ color: ['White', 'Yellow', 'Sky Blue', 'Beige', 'Silver', 'Tan'].includes(c.label) ? '#000' : '#FFF' }}
                    >
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] font-mono uppercase text-gray-500 mt-2 leading-tight">
            Pick a swatch or type a custom shade above (e.g. Charcoal, Two-tone).
          </p>
        </div>
      )}
    </div>
  );
};


const LuggageForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editBookingId = searchParams.get('edit');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Terms & conditions confirmation modal — shown before submit → payment.
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [editQuote, setEditQuote] = useState(null);
  // Which policy (if any) is being read inline inside the terms modal.
  // 'tnc' | 'insurance' | 'refund' | null
  const [activePolicy, setActivePolicy] = useState(null);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [deliveryLocations, setDeliveryLocations] = useState([]);

  const [route, setRoute] = useState({
    pickupLocation: '',
    pickupAddress: '',
    pickupArea: '',
    pickupLandmark: '',
    pickupPincode: '',
    deliveryLocation: '',
    deliveryAddress: '',
    deliveryArea: '',
    deliveryLandmark: '',
    deliveryPincode: '',
    deliveryPhone: ''
  });
  const [pickupSchedule, setPickupSchedule] = useState({ date: '', time: '' });

  // If the customer picks today's date AND their previously chosen slot has
  // already started, clear it so they're forced to pick a still-valid one.
  useEffect(() => {
    if (!pickupSchedule.date || !pickupSchedule.time) return;
    const slot = PICKUP_TIME_SLOTS.find(s => s.value === pickupSchedule.time);
    if (slot && isPickupSlotDisabled(slot, pickupSchedule.date)) {
      setPickupSchedule(prev => ({ ...prev, time: '' }));
    }
  }, [pickupSchedule.date, pickupSchedule.time]);

  const [bags, setBags] = useState([createNewBag()]);
  const [expandedBags, setExpandedBags] = useState({ 0: true });
  const { open: openPhoto, gallery: photoGallery } = usePhotoLightbox();
  // Per-bag AI detection state: 'idle' | 'running' | 'done' | 'error'
  const [detectState, setDetectState] = useState({});
  const [detectError, setDetectError] = useState({});
  const detectTimerRef = useRef({});

  useEffect(() => {
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      navigate('/');
      return;
    }
    fetchLocations();
    if (editBookingId) {
      fetchExistingBooking(editBookingId);
    }
  }, [navigate, editBookingId]);

  const [existingBooking, setExistingBooking] = useState(null);

  const fetchExistingBooking = async (bookingId) => {
    try {
      const uid = localStorage.getItem('user_id') || '';
      const response = await axios.get(`${API}/bookings/${bookingId}`, { params: { user_id: uid } });
      const b = response.data;
      setExistingBooking(b);
      setRoute({
        pickupLocation: b.pickup_location || '',
        pickupAddress: b.pickup_address || '',
        pickupArea: b.pickup_area || '',
        pickupLandmark: b.pickup_landmark || '',
        pickupPincode: b.pickup_pincode || '',
        deliveryLocation: b.delivery_location || '',
        deliveryAddress: b.delivery_address || '',
        deliveryArea: b.delivery_area || '',
        deliveryLandmark: b.delivery_landmark || '',
        deliveryPincode: b.delivery_pincode || '',
        deliveryPhone: b.delivery_phone || ''
      });
      setPickupSchedule({
        // Don't pre-fill a date that's now in the past — the date input has
        // `min={today}` so HTML5 validation would silently block submit and
        // the user would see nothing happen. Force them to pick a fresh slot.
        date: (b.scheduled_pickup_date && b.scheduled_pickup_date >= new Date().toISOString().split('T')[0])
          ? b.scheduled_pickup_date
          : '',
        time: (b.scheduled_pickup_date && b.scheduled_pickup_date >= new Date().toISOString().split('T')[0])
          ? (b.scheduled_pickup_time || '')
          : ''
      });
      const existingBags = b.luggage_details?.bags || [];
      if (existingBags.length > 0) {
        const prefilled = existingBags.map((bag, idx) => ({
          id: Date.now() + idx,
          bag_type: bag.bag_type || 'suitcase',
          brand: bag.brand || '',
          color: bag.color || '',
          material: bag.material || 'soft_fabric',
          weight: bag.weight ? String(bag.weight) : '',
          dimensions: bag.dimensions || '',
          handling_type: deriveHandlingFromWeight(bag.weight),
          lock_type: bag.lock_type || 'tsa',
          distinguishing_marks: bag.distinguishing_marks || '',
          declared_value: bag.declared_value ? String(bag.declared_value) : '',
          insurance_opted: !!bag.insurance_opted,
          images: bag.images || []
        }));
        setBags(prefilled);
        setExpandedBags({ 0: true });
      }
    } catch (err) {
      setError('Could not load existing booking for editing');
    }
  };

  const fetchLocations = async () => {
    try {
      const [pickupRes, deliveryRes] = await Promise.all([
        axios.get(`${API}/pickup-locations`),
        axios.get(`${API}/delivery-locations`)
      ]);
      setPickupLocations(pickupRes.data.locations);
      setDeliveryLocations(deliveryRes.data.locations);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    }
  };

  const handleRouteChange = (e) => {
    const { name, value } = e.target;
    setRoute(prev => {
      const next = { ...prev, [name]: value };
      // When the pickup OR destination city changes (or is cleared), wipe the
      // address bundle underneath so stale data from a different city never
      // reaches the backend.
      if (name === 'pickupLocation' && value !== prev.pickupLocation) {
        next.pickupAddress = '';
        next.pickupArea = '';
        next.pickupLandmark = '';
        next.pickupPincode = '';
      }
      if (name === 'deliveryLocation' && value !== prev.deliveryLocation) {
        next.deliveryAddress = '';
        next.deliveryArea = '';
        next.deliveryLandmark = '';
        next.deliveryPincode = '';
      }
      return next;
    });
    // Clear any lingering form-level error (e.g. "same city" message) as soon
    // as the user actively edits anything in the route block.
    if (error) setError('');
  };

  const handleBagChange = (index, field, value) => {
    setBags(prev => prev.map((b, i) => {
      if (i !== index) return b;
      const updated = { ...b, [field]: value };
      // Auto-derive handling category from weight whenever weight changes
      if (field === 'weight') {
        updated.handling_type = deriveHandlingFromWeight(value);
      }
      return updated;
    }));
  };

  const addBag = () => {
    const newBag = createNewBag();
    setBags(prev => [...prev, newBag]);
    setExpandedBags(prev => ({ ...prev, [bags.length]: true }));
  };

  const removeBag = (index) => {
    if (bags.length === 1) {
      setError('At least one bag is required');
      return;
    }
    setBags(prev => prev.filter((_, i) => i !== index));
  };

  const toggleBag = (index) => {
    setExpandedBags(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const compressImage = (file, maxDim = 1280, quality = 0.75) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onload = (ev) => {
        const img = new Image();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.onload = () => {
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            const ratio = width > height ? maxDim / width : maxDim / height;
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleBagImageUpload = async (bagIndex, e) => {
    const files = Array.from(e.target.files);
    const bag = bags[bagIndex];

    if (bag.images.length + files.length > 4) {
      setError(`Bag ${bagIndex + 1}: Max 4 images per bag`);
      e.target.value = '';
      return;
    }

    const MAX_BYTES = 10 * 1024 * 1024; // 10MB
    for (const file of files) {
      if (file.size > MAX_BYTES) {
        setError(`Bag ${bagIndex + 1}: "${file.name}" exceeds 10MB limit (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
        e.target.value = '';
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError(`Bag ${bagIndex + 1}: "${file.name}" is not a valid image`);
        e.target.value = '';
        return;
      }
    }

    setError('');
    try {
      for (const file of files) {
        const compressed = await compressImage(file);
        setBags(prev => prev.map((b, i) =>
          i === bagIndex ? { ...b, images: [...b.images, compressed] } : b
        ));
      }
      // Trigger AI attribute detection once the upload burst finishes.
      scheduleDetection(bagIndex);
    } catch (err) {
      setError(`Bag ${bagIndex + 1}: Failed to process image — ${err.message}`);
    }
    e.target.value = '';
  };

  const removeBagImage = (bagIndex, imgIndex) => {
    setBags(prev => prev.map((b, i) =>
      i === bagIndex ? { ...b, images: b.images.filter((_, idx) => idx !== imgIndex) } : b
    ));
    // If they remove the last photo, drop the "auto-filled" state
    setDetectState(prev => ({ ...prev, [bagIndex]: 'idle' }));
  };

  // Fire an AI attribute-detection call for a bag. Called (debounced) after
  // the customer finishes uploading photos. Does NOT overwrite fields the
  // customer has already typed — only fills the ones still blank / default.
  const detectBagAttributes = useCallback(async (bagIndex) => {
    setDetectError(prev => ({ ...prev, [bagIndex]: '' }));
    const currentBags = await new Promise(resolve => {
      setBags(prev => { resolve(prev); return prev; });
    });
    const bag = currentBags[bagIndex];
    if (!bag || !bag.images || bag.images.length === 0) return;

    setDetectState(prev => ({ ...prev, [bagIndex]: 'running' }));
    try {
      const resp = await axios.post(`${API}/luggage/detect-attributes`, {
        images: bag.images,
      }, { timeout: 45000 });
      const a = resp.data?.attributes || {};

      setBags(prev => prev.map((b, i) => {
        if (i !== bagIndex) return b;
        // Only overwrite fields that are still blank OR still at the initial
        // default (so a customer who has already typed a value keeps their input).
        const isBlank = (v) => v === undefined || v === null || String(v).trim() === '';
        return {
          ...b,
          bag_type: (b.bag_type === 'suitcase' || isBlank(b.bag_type)) && a.bag_type ? a.bag_type : b.bag_type,
          brand: isBlank(b.brand) && a.brand ? a.brand : b.brand,
          color: isBlank(b.color) && a.color ? a.color : b.color,
          material: (b.material === 'soft_fabric' || isBlank(b.material)) && a.material ? a.material : b.material,
          lock_type: (b.lock_type === 'tsa' || isBlank(b.lock_type)) && a.lock_type ? a.lock_type : b.lock_type,
          dimensions: isBlank(b.dimensions) && a.dimensions_estimate ? a.dimensions_estimate : b.dimensions,
          distinguishing_marks: isBlank(b.distinguishing_marks) && a.distinguishing_marks ? a.distinguishing_marks : b.distinguishing_marks,
          // Persist AI provenance so admin search can prioritise / annotate
          ai_detected: true,
          ai_confidence: a.confidence || 'medium',
        };
      }));
      setDetectState(prev => ({ ...prev, [bagIndex]: 'done' }));
    } catch (err) {
      console.error('[luggage] AI bag detection failed:', err);
      const msg = err?.response?.data?.detail || 'Could not auto-detect details from photos. Please fill them in manually.';
      setDetectError(prev => ({ ...prev, [bagIndex]: msg }));
      setDetectState(prev => ({ ...prev, [bagIndex]: 'error' }));
    }
  }, []);

  // Debounced trigger — waits 900 ms after the last photo lands to make sure
  // the customer isn't still mid-upload before we call the AI.
  const scheduleDetection = useCallback((bagIndex) => {
    if (detectTimerRef.current[bagIndex]) {
      clearTimeout(detectTimerRef.current[bagIndex]);
    }
    detectTimerRef.current[bagIndex] = setTimeout(() => {
      detectBagAttributes(bagIndex);
    }, 900);
  }, [detectBagAttributes]);

  const totals = {
    bags: bags.length,
    weight: bags.reduce((sum, b) => sum + (parseFloat(b.weight) || 0), 0),
    declared: bags.reduce((sum, b) => sum + (parseFloat(b.declared_value) || 0), 0)
  };

  // Auto-fill helpers (Google Places picks). We only overwrite a field if the
  // user hasn't already typed something there — so manual edits aren't blown
  // away if they pick a suggestion mid-way through corrections.
  const applyPickupAutofill = ({ street, area, pincode }) => {
    setRoute((prev) => ({
      ...prev,
      pickupAddress: street || prev.pickupAddress,
      pickupArea: prev.pickupArea?.trim() ? prev.pickupArea : (area || prev.pickupArea),
      pickupPincode: prev.pickupPincode?.trim() ? prev.pickupPincode : (pincode || prev.pickupPincode),
    }));
  };

  const applyDeliveryAutofill = ({ street, area, pincode }) => {
    setRoute((prev) => ({
      ...prev,
      deliveryAddress: street || prev.deliveryAddress,
      deliveryArea: prev.deliveryArea?.trim() ? prev.deliveryArea : (area || prev.deliveryArea),
      deliveryPincode: prev.deliveryPincode?.trim() ? prev.deliveryPincode : (pincode || prev.deliveryPincode),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Route validation
    if (!route.pickupLocation) return setError('Please select pickup city');
    if (!route.pickupAddress.trim()) return setError('Please enter pickup address');
    if (!route.pickupArea.trim()) return setError('Please enter pickup area');
    if (!/^\d{6}$/.test(route.pickupPincode)) return setError('Please enter a valid 6-digit pickup pincode');
    if (!route.deliveryLocation) return setError('Please select delivery city');
    if (!route.deliveryAddress.trim()) return setError('Please enter delivery address');
    if (!route.deliveryArea.trim()) return setError('Please enter delivery area');
    if (!/^\d{6}$/.test(route.deliveryPincode)) return setError('Please enter a valid 6-digit delivery pincode');
    if (!/^\d{10}$/.test((route.deliveryPhone || '').replace(/\D/g, ''))) {
      return setError('Please enter a valid 10-digit delivery phone number (OTP will be sent to this number).');
    }
    if (route.pickupLocation === route.deliveryLocation) {
      return setError('Pickup and delivery cities cannot be the same');
    }

    // Bag validation
    for (let i = 0; i < bags.length; i++) {
      const b = bags[i];
      if (!b.weight || parseFloat(b.weight) <= 0) return setError(`Bag ${i + 1}: Please enter a valid weight`);
      if (!b.color.trim()) return setError(`Bag ${i + 1}: Please enter color`);
      if (b.insurance_opted) {
        const dv = parseFloat(b.declared_value);
        if (!dv || dv <= 0) return setError(`Bag ${i + 1}: Please enter a declared value to opt in for insurance`);
        if (dv > 10000) return setError(`Bag ${i + 1}: Declared value cannot exceed ₹10,000 (max insurance cover)`);
      }
    }

    // All validations passed → ask the customer to confirm Terms, Insurance
    // and Refund policies before proceeding to Razorpay. For pure "SAVE
    // CHANGES" on already-paid bookings (no payment flow) we skip the modal.
    const ps = existingBooking?.payment_status;
    const pureSaveNoPayment = editBookingId && ['paid', 'top_up_pending', 'refund_pending', 'partially_refunded'].includes(ps);
    if (pureSaveNoPayment) {
      // Show the price difference (extra to pay / refund) before saving.
      try {
        setLoading(true);
        const q = await axios.post(`${API}/bookings/${editBookingId}/edit-quote`, buildBookingData());
        setEditQuote(q.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Could not calculate the new price. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }
    setShowTermsModal(true);
  };

  const buildBookingData = () => {
    const userId = localStorage.getItem('user_id');
    const userName = localStorage.getItem('user_name');

    return {
      user_id: userId,
      customer_name: userName,
      pickup_location: route.pickupLocation,
      pickup_address: route.pickupAddress,
      pickup_area: route.pickupArea,
      pickup_landmark: route.pickupLandmark,
      pickup_pincode: route.pickupPincode,
      delivery_location: route.deliveryLocation,
      delivery_address: route.deliveryAddress,
      delivery_area: route.deliveryArea,
      delivery_landmark: route.deliveryLandmark,
      delivery_pincode: route.deliveryPincode,
      delivery_phone: route.deliveryPhone,
      scheduled_pickup_date: pickupSchedule.date || '',
      scheduled_pickup_time: pickupSchedule.time || '',
      terms_accepted: true,
      luggage_details: {
        bags: bags.map(b => ({
          bag_type: b.bag_type,
          brand: b.brand,
          color: b.color,
          material: b.material,
          weight: parseFloat(b.weight),
          dimensions: b.dimensions,
          handling_type: b.handling_type,
          lock_type: b.lock_type,
          distinguishing_marks: b.distinguishing_marks,
          declared_value: b.insurance_opted ? (parseFloat(b.declared_value) || 0) : 0,
          insurance_opted: !!b.insurance_opted,
          images: b.images
        }))
      }
    };
  };

  // Runs the actual booking + payment flow. Extracted from handleSubmit so it
  // can be triggered from the Terms & Conditions confirmation modal below.
  const executeSubmit = async () => {
    setActivePolicy(null);
    setShowTermsModal(false);
    setLoading(true);

    try {
      const bookingData = buildBookingData();

      const response = editBookingId
        ? await axios.put(`${API}/bookings/${editBookingId}`, bookingData)
        : await axios.post(`${API}/bookings`, bookingData);
      const data = response.data || {};
      if (data.booking_id) {
        localStorage.setItem('booking_id', data.booking_id);
        localStorage.setItem('baggage_tag', data.baggage_tag);
        // Branch on the new backend response for edited paid bookings.
        if (editBookingId && data.payment_action === 'top_up' && data.top_up_amount > 0) {
          // Customer must pay the difference.
          try {
            if (PAYMENT_PROVIDER === 'razorpay') {
              // Refetch the latest booking so prefill + top_up_amount are correct.
              const fresh = await axios.get(`${API}/bookings/${data.booking_id}`, { params: { user_id: localStorage.getItem('user_id') || '' } });
              await openRazorpayCheckout({
                booking: fresh.data,
                purpose: 'top_up',
                onSuccess: () => { navigate('/dashboard?top_up_paid=1'); },
              });
              return;
            }
            const co = await axios.post(`${API}/payments/top-up-checkout`, {
              booking_id: data.booking_id,
              origin_url: window.location.origin
            });
            if (co.data?.url) {
              window.location.href = co.data.url;
              return;
            }
          } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || 'Edit saved, but could not open top-up payment. Please retry from your dashboard.';
            if (msg !== 'Payment cancelled') {
              setError(msg);
            } else {
              navigate('/dashboard');
            }
            return;
          }
        }
        if (editBookingId && data.payment_action === 'refund' && data.refund_amount > 0) {
          // Refund requested — go back to dashboard with toast hint via query.
          navigate(`/dashboard?refund_requested=${Number(data.refund_amount).toFixed(2)}`);
          return;
        }
        if (editBookingId && data.payment_action === 'none' && data.payment_status !== 'pending') {
          // Edited but no price change on an already-paid booking — return home.
          navigate('/dashboard?edit_saved=1');
          return;
        }
        // Default path (new booking or unpaid edit) — continue to payment.
        navigate(`/payment?booking_id=${data.booking_id}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || (editBookingId ? 'Failed to update booking' : 'Failed to create booking'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="theme-sunset p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <BrandMark size={36} />
          </div>
          <h1 className="text-4xl md:text-5xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk', lineHeight: '1' }}>
            {editBookingId ? 'EDIT BOOKING' : 'LUGGAGE MANIFEST'}
          </h1>
          <p className="text-sm text-gray-600 font-mono uppercase tracking-wide">
            {editBookingId
              ? (existingBooking && existingBooking.payment_status !== 'pending' ? 'UPDATE YOUR BOOKING' : 'UPDATE DETAILS BEFORE PAYMENT')
              : 'SHIPMENT DETAILS FORM'}
          </p>
          {editBookingId && (
            <button
              type="button"
              data-testid="luggage-form-back-btn"
              onClick={() => {
                const ps = existingBooking?.payment_status;
                if (ps === 'paid' || ps === 'top_up_pending' || ps === 'refund_pending' || ps === 'partially_refunded') {
                  navigate('/dashboard');
                } else {
                  navigate(`/payment?booking_id=${editBookingId}`);
                }
              }}
              className="btn-secondary mt-4 px-4 py-2 text-xs flex items-center gap-2"
            >
              <ArrowLeft size={14} />
              {(() => {
                const ps = existingBooking?.payment_status;
                return (ps === 'paid' || ps === 'top_up_pending' || ps === 'refund_pending' || ps === 'partially_refunded')
                  ? 'CANCEL EDIT & RETURN TO DASHBOARD'
                  : 'CANCEL EDIT & RETURN TO PAYMENT';
              })()}
            </button>
          )}
          {editBookingId && existingBooking && ['paid', 'top_up_pending', 'refund_pending', 'partially_refunded'].includes(existingBooking.payment_status) && (
            <div
              className="mt-4 p-3 border-2 border-black bg-[#FFF7E6]"
              data-testid="edit-paid-banner"
            >
              <div className="text-xs font-mono uppercase font-bold mb-1">EDITING A PAID BOOKING</div>
              <p className="text-xs font-mono leading-tight">
                Currently paid: <strong>₹{Number(existingBooking.amount_paid ?? existingBooking.amount ?? 0).toFixed(0)}</strong>. Before saving you&apos;ll see the new total and the exact extra amount to pay or refund you&apos;ll get.
              </p>
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          // Surface native HTML5 constraint failures (e.g. past date, missing
          // required field) so the customer is never left wondering why the
          // submit button looks dead.
          onInvalid={(e) => {
            const el = e.target;
            const label = (el.getAttribute('data-testid') || el.name || 'field').replace(/-/g, ' ');
            const msg = el.validationMessage || 'Invalid value';
            setError(`${label}: ${msg}`);
          }}
          data-testid="luggage-form"
          className="space-y-6"
        >
          {/* Route Section */}
          <div className="card-brutalist p-6">
            <div className="flex items-center gap-2 pb-2 border-b-2 border-black mb-4">
              <Navigation size={18} color="#FF4500" />
              <h2 className="text-sm font-bold uppercase font-mono tracking-wide">PICKUP DETAILS</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="data-label flex items-center gap-2 mb-2"><MapPin size={16} />PICKUP CITY</label>
                <select data-testid="pickup-location-select" name="pickupLocation" value={route.pickupLocation} onChange={handleRouteChange} className="w-full">
                  <option value="">SELECT CITY</option>
                  {pickupLocations.map((loc) => <option key={loc} value={loc}>{loc.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="data-label flex items-center gap-2 mb-2"><MapPin size={16} />PICKUP ADDRESS</label>
              <AddressAutocomplete
                testIdInput="pickup-address-input"
                name="pickupAddress"
                value={route.pickupAddress}
                onChange={handleRouteChange}
                onAutofill={applyPickupAutofill}
                cityHint={route.pickupLocation}
                placeholder="House / Building / Street"
                rows={2}
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="data-label mb-2 block">PICKUP AREA *</label>
                <input
                  data-testid="pickup-area-input"
                  name="pickupArea"
                  value={route.pickupArea}
                  onChange={handleRouteChange}
                  placeholder="Area / Locality"
                  className="w-full"
                />
              </div>
              <div>
                <label className="data-label mb-2 block">PICKUP LANDMARK <span className="text-gray-500 normal-case">(optional)</span></label>
                <input
                  data-testid="pickup-landmark-input"
                  name="pickupLandmark"
                  value={route.pickupLandmark}
                  onChange={handleRouteChange}
                  placeholder="Near…"
                  className="w-full"
                />
              </div>
              <div>
                <label className="data-label mb-2 block">PICKUP PINCODE *</label>
                <input
                  data-testid="pickup-pincode-input"
                  name="pickupPincode"
                  value={route.pickupPincode}
                  onChange={(e) => setRoute(prev => ({ ...prev, pickupPincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  placeholder="6-digit"
                  inputMode="numeric"
                  maxLength={6}
                  className="w-full"
                />
              </div>
            </div>

            {/* Pickup Schedule */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="data-label flex items-center gap-2 mb-2"><Calendar size={16} />PICKUP DATE</label>
                <input
                  data-testid="pickup-date-input"
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  value={pickupSchedule.date}
                  onChange={(e) => setPickupSchedule(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="data-label flex items-center gap-2 mb-2"><Clock size={16} />PICKUP TIME SLOT</label>
                <div data-testid="pickup-slot-group" className="grid grid-cols-1 gap-2">
                  {PICKUP_TIME_SLOTS.map(slot => {
                    const checked = pickupSchedule.time === slot.value;
                    const disabled = isPickupSlotDisabled(slot, pickupSchedule.date);
                    return (
                      <button
                        key={slot.value}
                        type="button"
                        data-testid={`pickup-slot-${slot.value.toLowerCase()}`}
                        disabled={disabled}
                        onClick={() => {
                          if (disabled) return;
                          // If switching to a future slot, clear stale errors and accept
                          setPickupSchedule(prev => ({ ...prev, time: slot.value }));
                        }}
                        title={disabled ? 'This slot has already started today. Pick a later slot or a future date.' : ''}
                        className={`flex items-center gap-2 px-3 py-2 border-2 border-black text-xs font-mono uppercase font-bold text-left transition-colors ${
                          disabled
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed line-through'
                            : checked
                              ? 'bg-[#00C853] text-black shadow-[3px_3px_0px_#0A0A0A]'
                              : 'bg-white text-black hover:bg-[#E8FFF0]'
                        }`}
                      >
                        <span className={`inline-block w-3 h-3 rounded-full border-2 ${disabled ? 'border-gray-400 bg-gray-200' : 'border-black ' + (checked ? 'bg-black' : 'bg-white')}`}></span>
                        <span className="flex-1">
                          {slot.label} <span className="text-gray-600 font-normal normal-case">({slot.range})</span>
                          {disabled && <span className="ml-2 text-[10px] not-italic font-normal normal-case text-gray-500">— slot has passed</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 font-mono mt-1">OPTIONAL — Your preferred pickup slot. Agent may contact you to confirm.</p>

            <div className="flex items-center gap-2 pb-2 border-b-2 border-black mb-4 mt-6">
              <Navigation size={18} color="#FF4500" style={{ transform: 'rotate(180deg)' }} />
              <h2 className="text-sm font-bold uppercase font-mono tracking-wide">DELIVERY DETAILS</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="data-label flex items-center gap-2 mb-2"><MapPin size={16} />DELIVERY CITY</label>
                <select data-testid="delivery-location-select" name="deliveryLocation" value={route.deliveryLocation} onChange={handleRouteChange} className="w-full">
                  <option value="">SELECT CITY</option>
                  {deliveryLocations.map((loc) => <option key={loc} value={loc}>{loc.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="data-label flex items-center gap-2 mb-2"><MapPin size={16} />DELIVERY ADDRESS</label>
              <AddressAutocomplete
                testIdInput="delivery-address-input"
                name="deliveryAddress"
                value={route.deliveryAddress}
                onChange={handleRouteChange}
                onAutofill={applyDeliveryAutofill}
                cityHint={route.deliveryLocation}
                placeholder="House / Building / Street"
                rows={2}
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="data-label mb-2 block">DELIVERY AREA *</label>
                <input
                  data-testid="delivery-area-input"
                  name="deliveryArea"
                  value={route.deliveryArea}
                  onChange={handleRouteChange}
                  placeholder="Area / Locality"
                  className="w-full"
                />
              </div>
              <div>
                <label className="data-label mb-2 block">DELIVERY LANDMARK <span className="text-gray-500 normal-case">(optional)</span></label>
                <input
                  data-testid="delivery-landmark-input"
                  name="deliveryLandmark"
                  value={route.deliveryLandmark}
                  onChange={handleRouteChange}
                  placeholder="Near…"
                  className="w-full"
                />
              </div>
              <div>
                <label className="data-label mb-2 block">DELIVERY PINCODE *</label>
                <input
                  data-testid="delivery-pincode-input"
                  name="deliveryPincode"
                  value={route.deliveryPincode}
                  onChange={(e) => setRoute(prev => ({ ...prev, deliveryPincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  placeholder="6-digit"
                  inputMode="numeric"
                  maxLength={6}
                  className="w-full"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="data-label mb-2 block">DELIVERY PHONE NUMBER *</label>
              <input
                data-testid="delivery-phone-input"
                name="deliveryPhone"
                value={route.deliveryPhone}
                onChange={(e) => setRoute(prev => ({ ...prev, deliveryPhone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                placeholder="10-digit mobile number"
                inputMode="numeric"
                maxLength={10}
                className="w-full"
              />
              <p className="text-xs text-gray-500 font-mono mt-1" data-testid="delivery-phone-caption">
                OTP will be triggered to this number while Delivering luggage.
              </p>
            </div>
          </div>

          {/* Bags Section */}
          <div className="card-brutalist p-6">
            <div className="flex items-center justify-between pb-2 border-b-2 border-black mb-4">
              <div className="flex items-center gap-2">
                <Briefcase size={18} color="#FF4500" />
                <h2 className="text-sm font-bold uppercase font-mono tracking-wide">BAG DETAILS</h2>
              </div>
              <div className="text-xs font-mono">
                <span className="text-gray-600">TOTAL:</span> <span className="font-bold">{totals.bags} BAG{totals.bags !== 1 ? 'S' : ''}</span>
                <span className="text-gray-600"> · </span>
                <span className="font-bold">{totals.weight.toFixed(1)} KG</span>
              </div>
            </div>

            <div className="space-y-4" data-testid="bags-list">
              {bags.map((bag, idx) => (
                <div key={bag.id} className="border-2 border-black bg-white" data-testid={`bag-${idx}`}>
                  {/* Bag Header (always visible) */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 border-b-2 border-black">
                    <button type="button" onClick={() => toggleBag(idx)} className="flex items-center gap-3 flex-1 text-left">
                      <div className="w-8 h-8 bg-[#FF4500] border-2 border-black flex items-center justify-center font-black text-sm">
                        {idx + 1}
                      </div>
                      <div>
                        <div className="font-bold text-sm font-mono uppercase">
                          BAG {idx + 1} — {BAG_TYPES.find(t => t.value === bag.bag_type)?.label || 'BAG'}
                        </div>
                        <div className="text-xs text-gray-600 font-mono">
                          {bag.brand && `${bag.brand} · `}{bag.color || 'No color'} · {bag.weight ? `${bag.weight} kg` : 'No weight'}
                        </div>
                      </div>
                      {expandedBags[idx] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {bags.length > 1 && (
                      <button
                        type="button"
                        data-testid={`remove-bag-${idx}`}
                        onClick={() => removeBag(idx)}
                        className="ml-2 p-2 border-2 border-black hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  {/* Bag Form (expanded) */}
                  {expandedBags[idx] && (
                    <div className="p-4 space-y-4">
                      {/* ── Step 1 · Photos + Pre-capture guidance ── */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="data-label flex items-center gap-2"><Camera size={14} />STEP 1 · BAG PHOTOS</label>
                          <span className="text-[10px] font-mono text-gray-600">{bag.images.length}/4</span>
                        </div>

                        {/* Pre-photo instructions — appear until the customer has uploaded at least 1 photo */}
                        {bag.images.length === 0 && (
                          <div
                            data-testid={`bag-photo-tips-${idx}`}
                            className="mb-3 p-3 border-2 border-[#FF4500] bg-orange-50 flex items-start gap-3"
                          >
                            <Camera size={20} className="text-[#FF4500] mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-gray-800 leading-relaxed">
                              <p className="font-bold uppercase tracking-wider text-[#FF4500] mb-1">Before you upload</p>
                              <ul className="list-disc list-inside space-y-0.5">
                                <li>Take up to <b>4 photos</b> of this bag</li>
                                <li>Capture <b>all angles</b> — front, back, top and any label</li>
                                <li>Use <b>good lighting</b> so the brand, color and lock are clearly visible</li>
                                <li>Once uploaded, we&apos;ll <b>auto-fill the details</b> using AI</li>
                              </ul>
                            </div>
                          </div>
                        )}

                        <div className="border-2 border-dashed border-black p-3 bg-gray-50">
                          {bag.images.length < 4 && (
                            <label className="btn-secondary px-3 py-1.5 inline-flex items-center gap-2 cursor-pointer text-xs">
                              <Upload size={14} />
                              {bag.images.length === 0 ? 'UPLOAD BAG PHOTOS' : 'ADD MORE PHOTOS'}
                              <input
                                data-testid={`bag-image-upload-${idx}`}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => handleBagImageUpload(idx, e)}
                                className="hidden"
                              />
                            </label>
                          )}
                          {bag.images.length > 0 && (
                            <div className="grid grid-cols-4 gap-2 mt-3">
                              {bag.images.map((img, imgIdx) => (
                                <div key={imgIdx} className="relative border-2 border-black aspect-square">
                                  <button
                                    type="button"
                                    onClick={() => openPhoto(bag.images, imgIdx)}
                                    aria-label={`Open photo ${imgIdx + 1} of bag ${idx + 1}`}
                                    className="block w-full h-full cursor-zoom-in"
                                  >
                                    <img src={img} alt={`Bag ${idx + 1} img ${imgIdx + 1}`} className="w-full h-full object-cover" />
                                  </button>
                                  <button
                                    type="button"
                                    data-testid={`remove-bag-${idx}-img-${imgIdx}`}
                                    onClick={(e) => { e.stopPropagation(); removeBagImage(idx, imgIdx); }}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 text-white border-2 border-black flex items-center justify-center hover:bg-red-700"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Manual-entry escape hatch when the customer hasn't uploaded anything yet */}
                        {bag.images.length === 0 && (
                          <button
                            type="button"
                            data-testid={`bag-skip-photos-${idx}`}
                            onClick={() => setDetectState(prev => ({ ...prev, [idx]: 'manual' }))}
                            className="mt-2 text-xs font-mono text-gray-600 underline hover:text-black"
                          >
                            Prefer to skip photos? Enter details manually →
                          </button>
                        )}
                      </div>

                      {/* ── Detection status banners ── */}
                      {bag.images.length > 0 && detectState[idx] === 'running' && (
                        <div
                          data-testid={`bag-detect-running-${idx}`}
                          className="p-3 border-2 border-blue-500 bg-blue-50 flex items-center gap-3 text-sm"
                        >
                          <Sparkles size={18} className="text-blue-600 animate-pulse flex-shrink-0" />
                          <div>
                            <div className="font-bold uppercase tracking-wider text-blue-700 text-xs">Reading your photos…</div>
                            <div className="text-gray-700 text-xs mt-0.5">Auto-detecting brand, color, material and lock type. Takes a few seconds.</div>
                          </div>
                        </div>
                      )}
                      {bag.images.length > 0 && detectState[idx] === 'done' && (
                        <div
                          data-testid={`bag-detect-done-${idx}`}
                          className="p-3 border-2 border-green-600 bg-green-50 flex items-start gap-3 text-sm"
                        >
                          <CheckCircle2 size={18} className="text-green-700 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-bold uppercase tracking-wider text-green-800 text-xs">Details auto-filled from your photos</div>
                            <div className="text-gray-700 text-xs mt-0.5">Please review below and <b>edit anything that&apos;s off</b>. You still need to enter the weight — that comes from your scale, not the photo.</div>
                          </div>
                        </div>
                      )}
                      {bag.images.length > 0 && detectState[idx] === 'error' && (
                        <div
                          data-testid={`bag-detect-error-${idx}`}
                          className="p-3 border-2 border-amber-500 bg-amber-50 flex items-start gap-3 text-sm"
                        >
                          <Info size={18} className="text-amber-700 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-bold uppercase tracking-wider text-amber-800 text-xs">Couldn&apos;t auto-detect</div>
                            <div className="text-gray-700 text-xs mt-0.5">{detectError[idx] || 'Please fill the details below manually.'}</div>
                            <button
                              type="button"
                              data-testid={`bag-detect-retry-${idx}`}
                              onClick={() => detectBagAttributes(idx)}
                              className="mt-1 text-xs font-mono underline text-amber-900 hover:text-black"
                            >
                              Retry detection
                            </button>
                          </div>
                        </div>
                      )}

                      {/* ── Step 2 · Details ── shown after photos are uploaded OR when the customer chose manual entry OR when the bag already has details (edit flow) */}
                      {(bag.images.length > 0 || detectState[idx] === 'manual' || !!bag.color || !!bag.weight || !!bag.brand) && (
                        <div>
                          <label className="data-label flex items-center gap-2 mb-3">
                            <Package size={14} />STEP 2 · BAG DETAILS
                            {detectState[idx] === 'done' && (
                              <span className="ml-2 text-[10px] font-mono px-2 py-0.5 bg-green-100 border border-green-600 text-green-800 rounded-sm inline-flex items-center gap-1">
                                <Sparkles size={10} /> AI-FILLED · EDITABLE
                              </span>
                            )}
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="data-label mb-2">BAG TYPE</label>
                              <select data-testid={`bag-type-${idx}`} value={bag.bag_type} onChange={(e) => handleBagChange(idx, 'bag_type', e.target.value)} className="w-full">
                                {BAG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="data-label mb-2">BRAND / COMPANY</label>
                              <input data-testid={`bag-brand-${idx}`} type="text" value={bag.brand} onChange={(e) => handleBagChange(idx, 'brand', e.target.value)} placeholder="e.g. Samsonite, American Tourister" className="w-full" />
                            </div>
                            <div className="col-span-1 sm:col-span-2 md:col-span-3">
                              <label className="data-label mb-2">COLOR <span className="text-red-600">*</span></label>
                              <BagColorPicker
                                idx={idx}
                                value={bag.color}
                                onChange={(v) => handleBagChange(idx, 'color', v)}
                              />
                            </div>
                            <div>
                              <label className="data-label mb-2">MATERIAL</label>
                              <select data-testid={`bag-material-${idx}`} value={bag.material} onChange={(e) => handleBagChange(idx, 'material', e.target.value)} className="w-full">
                                {MATERIALS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="data-label mb-2">WEIGHT (KG) <span className="text-red-600">*</span></label>
                              <input data-testid={`bag-weight-${idx}`} type="number" step="0.1" min="0.1" value={bag.weight} onChange={(e) => handleBagChange(idx, 'weight', e.target.value)} placeholder="0.0" className="w-full" />
                            </div>
                            <div>
                              <label className="data-label mb-2">DIMENSIONS (L×W×H CM) <span className="text-gray-500 normal-case">(optional)</span></label>
                              <input data-testid={`bag-dimensions-${idx}`} type="text" value={bag.dimensions} onChange={(e) => handleBagChange(idx, 'dimensions', e.target.value)} placeholder="50×30×20" className="w-full" />
                            </div>
                            <div>
                              <label className="data-label mb-2">HANDLING CATEGORY <span className="text-gray-500 normal-case">(auto from weight)</span></label>
                              <select data-testid={`bag-handling-${idx}`} value={bag.handling_type} onChange={(e) => handleBagChange(idx, 'handling_type', e.target.value)} className="w-full">
                                {HANDLING_TYPES.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="data-label mb-2">LOCK TYPE</label>
                              <select data-testid={`bag-lock-${idx}`} value={bag.lock_type} onChange={(e) => handleBagChange(idx, 'lock_type', e.target.value)} className="w-full">
                                {LOCK_TYPES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                              </select>
                            </div>
                            <div className="md:col-span-2">
                              <label className="data-label mb-2">DISTINGUISHING MARKS / IDENTIFIERS</label>
                              <input data-testid={`bag-marks-${idx}`} type="text" value={bag.distinguishing_marks} onChange={(e) => handleBagChange(idx, 'distinguishing_marks', e.target.value)} placeholder="e.g. Yellow ribbon on handle, sticker on top" className="w-full" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="data-label mb-2 flex items-center gap-2">
                                <Shield size={13} className="text-[#00C853]" />
                                INSURANCE COVERAGE — OPT IN?
                              </label>
                              <div className="grid grid-cols-2 gap-2 mb-3" data-testid={`bag-insurance-toggle-${idx}`}>
                                <button
                                  type="button"
                                  data-testid={`bag-insurance-yes-${idx}`}
                                  onClick={() => {
                                    handleBagChange(idx, 'insurance_opted', true);
                                  }}
                                  className={`px-3 py-2 border-2 border-black text-xs font-mono uppercase font-bold text-left transition-colors flex items-center gap-2 ${
                                    bag.insurance_opted
                                      ? 'bg-[#00C853] text-black shadow-[3px_3px_0px_#0A0A0A]'
                                      : 'bg-white text-black hover:bg-[#E8FFF0]'
                                  }`}
                                >
                                  <span className={`inline-block w-3 h-3 rounded-full border-2 border-black ${bag.insurance_opted ? 'bg-black' : 'bg-white'}`}></span>
                                  <span>YES, INSURE THIS BAG</span>
                                </button>
                                <button
                                  type="button"
                                  data-testid={`bag-insurance-no-${idx}`}
                                  onClick={() => {
                                    handleBagChange(idx, 'insurance_opted', false);
                                    handleBagChange(idx, 'declared_value', '');
                                  }}
                                  className={`px-3 py-2 border-2 border-black text-xs font-mono uppercase font-bold text-left transition-colors flex items-center gap-2 ${
                                    !bag.insurance_opted
                                      ? 'bg-gray-900 text-white shadow-[3px_3px_0px_#0A0A0A]'
                                      : 'bg-white text-black hover:bg-gray-100'
                                  }`}
                                >
                                  <span className={`inline-block w-3 h-3 rounded-full border-2 ${!bag.insurance_opted ? 'bg-white border-white' : 'border-black bg-white'}`}></span>
                                  <span>NO, SKIP INSURANCE</span>
                                </button>
                              </div>

                              {bag.insurance_opted ? (
                                <div className="border-2 border-black bg-[#E8FFF0] p-3">
                                  <label className="data-label mb-2 block">DECLARED VALUE (₹) — MAX ₹10,000</label>
                                  <input
                                    data-testid={`bag-value-${idx}`}
                                    type="number"
                                    min="1"
                                    max="10000"
                                    step="1"
                                    value={bag.declared_value}
                                    onChange={(e) => handleBagChange(idx, 'declared_value', e.target.value)}
                                    placeholder="e.g. 8000"
                                    className="w-full"
                                  />
                                  {(() => {
                                    const dv = Math.min(parseFloat(bag.declared_value) || 0, 10000);
                                    const premium = (dv * 0.04).toFixed(2);
                                    return (
                                      <p className="text-xs font-mono mt-2 leading-snug">
                                        <span className="text-neutral-600">INSURANCE PREMIUM (4% OF DECLARED VALUE):</span>{' '}
                                        <span className="font-bold text-[#00C853]">+ ₹{premium}</span>
                                        <br />
                                        <span className="text-[10px] text-neutral-500">Added to final payment. Max cover ₹10,000 per bag.</span>
                                      </p>
                                    );
                                  })()}
                                </div>
                              ) : (
                                <p className="text-xs font-mono text-neutral-500 leading-snug">
                                  No insurance will be added for this bag. You can still track and claim status via support.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              data-testid="add-bag-btn"
              onClick={addBag}
              className="mt-4 btn-secondary w-full py-3 flex items-center justify-center gap-2"
            >
              <Plus size={18} />
              ADD ANOTHER BAG
            </button>
          </div>

          {/* Error */}
          {error && (
            <p data-testid="form-error" className="text-red-600 text-sm font-mono p-4 border-2 border-red-600 bg-red-50">
              {error}
            </p>
          )}

          {/* Submit */}
          <button
            data-testid="proceed-payment-btn"
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {loading
              ? 'PROCESSING...'
              : editBookingId
                ? (existingBooking && ['paid', 'top_up_pending', 'refund_pending', 'partially_refunded'].includes(existingBooking.payment_status) ? 'SAVE CHANGES' : 'SAVE & RETURN TO PAYMENT')
                : 'PROCEED TO PAYMENT'}
            <ArrowRight size={24} />
          </button>
        </form>
      </div>
      {photoGallery}

      {/* ============== TERMS CONFIRMATION MODAL ============== */}
      {editQuote && (
        <EditPriceConfirmModal
          quote={editQuote}
          busy={loading}
          onCancel={() => setEditQuote(null)}
          onConfirm={() => { setEditQuote(null); executeSubmit(); }}
        />
      )}
      {showTermsModal && (
        <div
          data-testid="terms-confirm-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              if (activePolicy) setActivePolicy(null);
              else setShowTermsModal(false);
            }
          }}
          role="dialog"
          aria-modal="true"
        >
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => { setActivePolicy(null); setShowTermsModal(false); }}
          />

          {/* card */}
          <div
            className="relative bg-white border-2 border-black w-full max-w-lg shadow-[8px_8px_0px_#0A0A0A] animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header stripe */}
            <div className="bg-[#FF5A1F] border-b-2 border-black px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-black flex items-center justify-center border-2 border-black shrink-0">
                <Shield size={20} className="text-[#FF5A1F]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-widest text-black/70">
                  ONE LAST THING
                </div>
                <h3 className="text-lg sm:text-xl font-black uppercase leading-tight" style={{ fontFamily: 'IBM Plex Mono' }}>
                  CONFIRM TO PROCEED
                </h3>
              </div>
              <button
                onClick={() => { setActivePolicy(null); setShowTermsModal(false); }}
                aria-label="Close"
                className="w-9 h-9 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors flex items-center justify-center shrink-0"
                data-testid="terms-modal-close-btn"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <p className="text-[15px] leading-relaxed text-neutral-800">
                By proceeding to payment, you agree to the following policies of
                <strong className="whitespace-nowrap"> Luggagefreejourney</strong>:
              </p>

              {/* Policy chips */}
              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={() => setActivePolicy('tnc')}
                  className="group flex items-start gap-3 border-2 border-black bg-white p-3 hover:bg-[#FFF7E6] transition-colors text-left"
                  data-testid="terms-link-tnc"
                >
                  <div className="w-8 h-8 bg-black text-white flex items-center justify-center shrink-0">
                    <FileText size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-mono uppercase font-bold tracking-wider text-black">
                      Terms &amp; Conditions
                    </div>
                    <p className="text-[13px] text-neutral-600 mt-0.5 leading-snug">
                      Service rules, permitted items and customer obligations.
                    </p>
                  </div>
                  <ArrowRight size={14} className="mt-2 shrink-0 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </button>

                <button
                  type="button"
                  onClick={() => setActivePolicy('insurance')}
                  className="group flex items-start gap-3 border-2 border-black bg-white p-3 hover:bg-[#EAF7ED] transition-colors text-left"
                  data-testid="terms-link-insurance"
                >
                  <div className="w-8 h-8 bg-[#00C853] text-black flex items-center justify-center shrink-0 border-r-2 border-black">
                    <Shield size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-mono uppercase font-bold tracking-wider text-black">
                      Insurance Coverage · Up to ₹10,000
                    </div>
                    <p className="text-[13px] text-neutral-600 mt-0.5 leading-snug">
                      Insurance is optional per bag (4% of declared value, cover up to ₹10,000).
                    </p>
                  </div>
                  <ArrowRight size={14} className="mt-2 shrink-0 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </button>

                <button
                  type="button"
                  onClick={() => setActivePolicy('refund')}
                  className="group flex items-start gap-3 border-2 border-black bg-white p-3 hover:bg-[#FFEDE5] transition-colors text-left"
                  data-testid="terms-link-refund"
                >
                  <div className="w-8 h-8 bg-[#FF5A1F] text-black flex items-center justify-center shrink-0 border-r-2 border-black">
                    <RotateCcw size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-mono uppercase font-bold tracking-wider text-black">
                      Cancellation &amp; Refund Policy
                    </div>
                    <p className="text-[13px] text-neutral-600 mt-0.5 leading-snug">
                      Cancellation timelines, admin-approved refunds and top-ups.
                    </p>
                  </div>
                  <ArrowRight size={14} className="mt-2 shrink-0 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </button>
              </div>

              <p className="text-[12px] text-neutral-500 leading-snug font-mono">
                Tap <strong className="text-black">OK, PROCEED</strong> to confirm and continue to payment.
              </p>
            </div>

            {/* Footer buttons */}
            <div className="px-6 pb-6 pt-2 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end border-t-2 border-black sm:border-t-0">
              <button
                onClick={() => { setActivePolicy(null); setShowTermsModal(false); }}
                className="btn-secondary px-6 py-3 text-sm font-bold uppercase font-mono"
                data-testid="terms-modal-cancel-btn"
              >
                CANCEL
              </button>
              <button
                onClick={executeSubmit}
                disabled={loading}
                className="btn-primary px-6 py-3 text-sm font-bold uppercase font-mono flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="terms-modal-confirm-btn"
                autoFocus
              >
                <CheckCircle2 size={16} />
                OK, PROCEED
              </button>
            </div>

            {/* Inline policy viewer — layered OVER the modal card so the user
                stays on this page and can CLOSE back to the OK/CANCEL footer.
                Uses an iframe to the live /terms route (same origin, same tab,
                session fully preserved) so content stays in a single source. */}
            {activePolicy && (
              <div
                className="absolute inset-0 bg-white flex flex-col"
                data-testid="policy-inline-viewer"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-5 py-3 bg-black text-white border-b-2 border-black flex items-center justify-between gap-3 shrink-0">
                  <div className="font-mono uppercase text-sm font-bold tracking-wider truncate">
                    {activePolicy === 'tnc' && 'Terms & Conditions'}
                    {activePolicy === 'insurance' && 'Insurance Coverage'}
                    {activePolicy === 'refund' && 'Cancellation & Refund Policy'}
                  </div>
                  <button
                    type="button"
                    onClick={() => setActivePolicy(null)}
                    data-testid="policy-inline-close"
                    className="flex items-center gap-2 px-3 py-1.5 border-2 border-white hover:bg-white hover:text-black text-xs font-mono font-bold uppercase transition-colors"
                    autoFocus
                  >
                    <X size={14} />
                    CLOSE
                  </button>
                </div>
                <iframe
                  key={activePolicy}
                  src={
                    activePolicy === 'insurance' ? '/terms#insurance'
                    : activePolicy === 'refund' ? '/terms#refund'
                    : '/terms'
                  }
                  title="Policy"
                  className="flex-1 w-full border-0"
                  data-testid="policy-inline-iframe"
                />
                <div className="px-5 py-2 border-t-2 border-black bg-gray-50 text-[11px] font-mono text-neutral-600 text-center shrink-0">
                  Tap CLOSE to return to payment confirmation.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LuggageForm;
